import React, { useState, useEffect } from "react";
import { TbEdit } from "react-icons/tb";
import { toast } from "react-toastify";

const Version = ({ isSidebarCollapsed }) => {
  const [versions, setVersions] = useState([]);
  const [formData, setFormData] = useState({
    version: "",
    url: "",
  });
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [submitState, setSubmitState] = useState(false);
  const Auth = localStorage.getItem("authToken");

  useEffect(() => {
    fetchVersions();
  }, []);

  const fetchVersions = async () => {
    try {
      const response = await fetch("https://backend.sanchalansetu.com/api/v1/version", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Auth}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch versions");

      const result = await response.json();
      if (Array.isArray(result)) {
        setVersions(result);
      } else {
        setVersions([result]);
      }

      setLoading(false);
    } catch (err) {
      toast.error("Error loading versions");
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    const itemToEdit = versions.find((v) => v.id === id);
    if (itemToEdit) {
      setFormData({
        version: itemToEdit.version,
        url: itemToEdit.url,
      });
      setEditId(id);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editId) {
      toast.warning("Please select a version to update");
      return;
    }

    setSubmitState(true);
    try {
      const response = await fetch(
        `https://backend.sanchalansetu.com/api/v1/version/edit/${editId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Auth}`,
          },
          body: JSON.stringify(formData),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        toast.error(result.message || "Failed to update version");
      } else {
        toast.success(result.message || "Version updated successfully!");
        setEditId(null);
        setFormData({ version: "", url: "" });
        fetchVersions();
      }
    } catch {
      toast.error("Something went wrong while updating");
    } finally {
      setSubmitState(false);
    }
  };

  const cancelEdit = () => {
    setFormData({ version: "", url: "" });
    setEditId(null);
  };

  if (loading) return <h2 className="text-left">Loading...</h2>;

  return (
    <div className="role-settings-container">
      <div className="bg-white p-[1vw] role-settings-form-container">
        <div className="text-[20px] font-bold text-gray-600 border-b border-gray-300 pb-1">
          <h1>{editId ? "Update Version" : "Select a Version to Edit"}</h1>
        </div>

        <form className="bg-white pt-2 rounded-lg" onSubmit={handleSubmit}>
          <div className="mb-2">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Version *
            </label>
            <input
              name="version"
              value={formData.version}
              onChange={handleInputChange}
              type="text"
              placeholder="Enter Version"
              className="shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
              required
            />
          </div>

          <div className="mb-2">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              URL *
            </label>
            <input
              name="url"
              value={formData.url}
              onChange={handleInputChange}
              type="text"
              placeholder="Enter URL"
              className="shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
              required
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              type="submit"
              className="bg-blue-500 text-sm hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              disabled={submitState}
            >
              {submitState ? "Updating..." : "Update"}
            </button>
            {editId && (
              <button
                type="button"
                onClick={cancelEdit}
                className="bg-gray-500 text-sm hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <ul className="w-full role-settings-display-container">
        <li className="bg-white p-[1vw]">
          <div className="flex justify-between flex-wrap">
            <h1 className="text-[20px] font-bold pb-[10px]">Version List</h1>
          </div>

          <section className="flex flex-col justify-center">
            <div className="h-full">
              <div className="w-full bg-white">
                <div className="overflow-x-auto">
                  <table className="table-auto w-full">
                    <thead className="font-semibold uppercase text-white bg-blue-500">
                      <tr className="grid text-xs grid-cols-4">
                        <th className="px-4 py-2 text-left">#</th>
                        <th className="px-4 py-2 text-left">Version</th>
                        <th className="px-4 py-2 text-left">URL</th>
                        <th className="px-4 py-2 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm divide-y divide-gray-100">
                      {versions.length > 0 ? (
                        versions.map((v, index) => (
                          <tr className="grid grid-cols-4" key={v.id}>
                            <td className="px-4 py-2">{index + 1}</td>
                            <td className="px-4 py-2">{v.version || "N/A"}</td>
                            <td className="px-4 py-2">{v.url || "N/A"}</td>
                            <td className="px-4 py-2 text-center">
                              <button
                                onClick={() => handleEdit(v.id)}
                                className="text-blue-500 hover:text-blue-700 text-[20px]"
                                title="Edit"
                              >
                                <TbEdit />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="p-2 text-center">
                            No Version Data Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        </li>
      </ul>
    </div>
  );
};

export default Version;
